import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
} from '../styles';
import { Container } from './ui';
import PardotForm from './PardotForm';

const StyledNewsletterSignup = styled.section``;

const StyledContainer = styled(Container)`
  ${({ isBlogDetail }) => {
    if (!isBlogDetail) {
      return css`
        max-width: 100%;
        padding-left: 0;
        padding-right: 0;
      `;
    } else {
      return css`
        ${maxBreakpointQuery.tiny`
          padding-left: 0;
          padding-right: 0;
        `}
      `;
    }
  }}
`;

const StyledOuter = styled.div`
  background-color: ${brandColours.primary};
  ${({ isBlogDetail }) => {
    if (isBlogDetail) {
      return css`
        border-radius: 20px;
        ${sectionPaddings('40px', '70px')};
      `;
    } else {
      return css`
        ${sectionPaddings('40px', '140px')};
      `;
    }
  }}
`;

const StyledInner = styled.div`
  ${({ isBlogDetail }) => {
    if (!isBlogDetail) {
      return css`
        display: grid;
        gap: 40px;

        ${minBreakpointQuery.small`
          gap: 60px;
        `}

        ${minBreakpointQuery.smedium`
          grid-template-columns: 1.2fr 1fr;
        `}

        ${minBreakpointQuery.medium`
          gap: 80px;
        `}

        ${minBreakpointQuery.large`
          gap: 100px;
        `}

        ${minBreakpointQuery.xxlarge`
          gap: 120px;
        `}

        ${minBreakpointQuery.xxxlarge`
          gap: 140px;
        `}
      `;
    }
  }}
`;

const StyledHeading = styled.h2`
  margin-bottom: 15px;

  ${({ isBlogDetail }) => {
    if (!isBlogDetail) {
      return css`
        ${fluidFontSize(
          '25px',
          '40px',
          breakpointSizes.tiny,
          breakpointSizes.xxxxlarge,
        )};
      `;
    } else {
      return css`
        ${fontSize(21)};
        line-height: 1.4;
        font-weight: ${fontWeights.semibold};
        color: ${brandColours.secondary};

        ${minBreakpointQuery.small`
          ${fontSize(23)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(25)};
        `};
      `;
    }
  }}
`;

const StyledText = styled.p`
  white-space: ${({ isBlogDetail }) =>
    isBlogDetail ? 'normal' : 'break-spaces'};
  line-height: 1.5;
  margin-bottom: 15px;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 560px;

  ${minBreakpointQuery.smedium`
    grid-area: 1/2/1/2;
  `}
`;

const NewsletterSignup = ({ isBlogDetail }) => {
  const {
    datoCmsNewsletterSignupSitewide: {
      heading,
      text,
      image,
      pardotFormUrl,
      pardotFormHeight,
    },
  } = useStaticQuery(graphql`
    query NewsletterSignupQuery {
      datoCmsNewsletterSignupSitewide {
        heading
        text
        image {
          url
          alt
        }
        pardotFormUrl
        pardotFormHeight
      }
    }
  `);

  return (
    <StyledNewsletterSignup>
      <StyledContainer narrower={isBlogDetail} isBlogDetail={isBlogDetail}>
        <StyledOuter isBlogDetail={isBlogDetail}>
          <Container narrowest={isBlogDetail}>
            <StyledInner isBlogDetail={isBlogDetail}>
              {!isBlogDetail && (
                <StyledImage src={image.url} alt={image.alt} loading="lazy" />
              )}
              <div>
                <StyledHeading isBlogDetail={isBlogDetail}>
                  {heading}
                </StyledHeading>
                <StyledText isBlogDetail={isBlogDetail}>{text}</StyledText>
                <PardotForm url={pardotFormUrl} height={pardotFormHeight} />
              </div>
            </StyledInner>
          </Container>
        </StyledOuter>
      </StyledContainer>
    </StyledNewsletterSignup>
  );
};

export default NewsletterSignup;
